import React, { ForwardRefRenderFunction, forwardRef } from "react";
//#region types
import { FormHandles } from "models/propTypes/genericFormProps";
import { AgreementFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { AgreementCreateFormContainerProps } from "models/propTypes/agreementPropTypes";
import { UploadedFile } from "models/utilTypes/fileUtilsTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
//#endregion
//#region components
import AgreementCreateForm from "../../agreement/forms/createForm";
import { LoadingWithLogo, useMountedState } from "components/common";
import { useAgreementService } from "serviceHooks";
//#endregion
import * as Yup from "yup";
import { DateHelper } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const AgreementCreateFormContainer: ForwardRefRenderFunction<FormHandles, AgreementCreateFormContainerProps> = (
  props,
  ref
) => {
  const agreementService = useAgreementService();
  const { agreementId, nonEditable } = props;
  const { getMounted } = useMountedState();
  const [loading, setLoading] = React.useState(false);

  const [initialValues, setInitialValues] = React.useState<AgreementFormTypes.Create>({
    active: true,
    start: DateHelper.today(),
  });

  React.useEffect(() => {
    if (agreementId) {
      loadAgreement(agreementId);
    }
  }, [agreementId]);

  const loadAgreement = (id: number) => {
    props.onFormValidationChange && props.onFormValidationChange(false);
    setLoading(true);
    agreementService
      .getById(id)
      .then((agreement) => {
        if (getMounted()) {
          setInitialValues({
            name: agreement.name,
            counterPart: agreement.counterPart,
            counterPartOrgNr: agreement.counterPartOrgNr,
            description: agreement.description,
            start: DateHelper.resetTime(agreement.start) ?? DateHelper.today(),
            end: DateHelper.resetTime(agreement.end),
            noticeMonths: agreement.noticeMonths,
            monthlyCost: agreement.monthlyCost,
            active: agreement.active ?? true,
            filesToUpload: agreement.attachments
              ? agreement.attachments?.map(
                  (file): UploadedFile => ({
                    fileName: file.name ?? "",
                    id: file.id,
                    data: file.data ?? "",
                    contentType: file.contentType ?? "",
                  })
                )
              : [],
          });
          props.onFormValidationChange && props.onFormValidationChange(true);
        }
      })
      .finally(() => setLoading(false));
  };

  const validationSchema = Yup.object<AgreementFormTypes.Create>({
    name: Yup.string(),
    description: Yup.string(),
    start: Yup.date().typeError(TranslationKeys.Validations.InvalidDate).required(TranslationKeys.Validations.Required),
    end: Yup.date().typeError(TranslationKeys.Validations.InvalidDate),
    noticeMonths: Yup.number(),
    monthlyCost: Yup.number(),
    counterPart: Yup.string(),
    counterPartOrgNr: Yup.string(),
    active: Yup.boolean().required(TranslationKeys.Validations.Required),
    filesToUpload: Yup.array().of(
      Yup.object<UploadedFile>({
        contentType: Yup.string(),
        fileName: Yup.string(),
        fileSize: Yup.number(),
        data: Yup.string(),
      })
    ),
  });

  const handleSubmit = async (values: AgreementFormTypes.Create) => {
    const agreement: WintSuperkollAPIModel.AgreementPost = {
      name: values.name,
      counterPart: values.counterPart,
      counterPartOrgNr: values.counterPartOrgNr,
      description: values.description,
      start: DateHelper.resetTime(values.start) ?? DateHelper.today(),
      end: DateHelper.resetTime(values.end),
      noticeMonths: values.noticeMonths,
      monthlyCost: values.monthlyCost,
      active: values.active ?? true,
      attachments: values.filesToUpload?.map(
        (file): WintSuperkollAPIModel.AgreementAttachment => ({
          name: file.fileName,
          contentType: file.contentType,
          id: Number(file.id ?? 0),
          data: file.data,
        })
      ),
    };
    const result = agreementId
      ? await agreementService.update(agreement, agreementId)
      : await agreementService.create(agreement);

    result?.data && props.onFormSubmitted && props.onFormSubmitted(result.data);
  };

  return (
    <>
      {loading && (
        <div className={cssExports.dialogLoadingContainer}>
          <LoadingWithLogo />
        </div>
      )}
      <AgreementCreateForm
        ref={ref}
        {...props}
        validationSchema={validationSchema}
        defaultValues={initialValues}
        handleSubmit={handleSubmit}
        nonEditable={nonEditable}
        onFormValidationChange={props.onFormValidationChange}
      />
    </>
  );
};

export default forwardRef(AgreementCreateFormContainer);
